import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillWave, faCalendarAlt, faTag, faArrowUp,
  faLock, faCheckCircle, faTimesCircle, faListOl,
  faExchangeAlt, faSearch, faExclamationTriangle,
  faEnvelope, faPhone, faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import '../assets/css/legal-pages.css';

const RefundPolicy = () => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="legal-page">
      {/* Hero Header */}
      <div className="legal-header">
        <FontAwesomeIcon icon={faMoneyBillWave} className="legal-header-icon" />
        <h1>Refund Policy</h1>
        <div className="legal-header-meta">
          <span><FontAwesomeIcon icon={faCalendarAlt} /> Effective Date: 6th March, 2026</span>
          <span><FontAwesomeIcon icon={faTag} /> Version 1.0</span>
        </div>
        <div className="legal-intro">
          This Refund Policy governs all refund and dispute resolution processes on the AutoboyExpress platform for both standard purchases and swap transactions. By using AutoboyExpress, you agree to this Refund Policy.
        </div>
      </div>

      {/* 1. Overview */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faMoneyBillWave} /> 1. Overview</h2>
        <p>AutoboyExpress operates a secure payment system powered by Monnify, with escrow-protected order payments held by Pandascrow. All funds paid by buyers are held and are not released to the seller until the transaction is confirmed complete or a dispute is resolved. This policy outlines the conditions under which refunds are issued and the process for raising and resolving disputes.</p>
      </div>

      {/* 2. Escrow and Auto-Release */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faLock} /> 2. Escrow and Auto-Release</h2>
        <p>All payments made by buyers on AutoboyExpress are held in escrow by Pandascrow, our licensed third-party escrow provider. Pandascrow — not AutoboyExpress — holds the funds on behalf of both parties until the transaction is complete.</p>
        <ul>
          <li>Funds are released to the seller once the buyer confirms delivery, or once the inspection period elapses without a dispute being raised (auto-release).</li>
          <li>If a buyer takes no action within the inspection window after confirmed delivery, the escrowed funds automatically release to the seller.</li>
          <li>If a dispute is raised within the inspection window, the funds remain held in escrow and are not released to either party until the dispute is resolved.</li>
          <li>Disputed transactions are reviewed and resolved directly through Pandascrow's dispute resolution process; refunds on disputed escrow transactions are issued by Pandascrow, not by manual action from AutoboyExpress.</li>
        </ul>
      </div>

      {/* 3. Eligible Refund Conditions */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faCheckCircle} /> 3. Eligible Refund Conditions</h2>
        <p>A buyer may be eligible for a refund under the following conditions:</p>
        <ul>
          <li>The gadget received is significantly different from the listing description or photographs.</li>
          <li>The gadget received is in a worse condition than described by the seller.</li>
          <li>The gadget received is non-functional or has undisclosed faults.</li>
          <li>The wrong item was delivered.</li>
          <li>The gadget was damaged during the logistics and delivery process.</li>
          <li>The dispute is raised within the 3-day window after confirmed delivery.</li>
        </ul>
      </div>

      {/* 4. Non-Refundable Conditions */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faTimesCircle} /> 4. Non-Refundable Conditions</h2>
        <p>Refunds will not be issued under the following circumstances:</p>
        <ul>
          <li>The 3-day dispute window has passed without a dispute being raised.</li>
          <li>The buyer simply changed their mind after receiving the gadget.</li>
          <li>The gadget matches the listing description and photographs provided by the seller.</li>
          <li>The issue was disclosed by the seller in the listing and the buyer accepted it at checkout.</li>
          <li>The dispute was found to be fraudulent or made in bad faith after investigation.</li>
        </ul>
      </div>

      {/* 5. How to Raise a Dispute */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faListOl} /> 5. How to Raise a Dispute</h2>
        <p>To raise a dispute and request a refund, follow these steps:</p>
        <ol>
          <li>Log into your AutoboyExpress account <strong>within 3 days of confirmed delivery</strong>.</li>
          <li>Navigate to the order in question and tap <strong>'Raise a Dispute'</strong>.</li>
          <li>Select the reason for your dispute from the available options.</li>
          <li>Upload supporting evidence including photographs, videos, and a written description of the issue.</li>
          <li>Submit your dispute. Funds related to this transaction remain held in escrow (via Pandascrow, where applicable) and will not be released to either party until the dispute is resolved.</li>
          <li>AutoboyExpress admin will review the evidence from both parties and communicate a resolution.</li>
        </ol>
      </div>

      {/* 6. Standard Purchase Refunds */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faMoneyBillWave} /> 6. Standard Purchase Refunds</h2>
        <p>For standard purchase transactions:</p>
        <ul>
          <li><strong>If the dispute is ruled in the buyer's favour</strong> — a full refund of the product price, logistics fee, and VAT is processed to the buyer's original payment method via Monnify (or Pandascrow, for escrow-protected orders).</li>
          <li><strong>If the dispute is ruled in the seller's favour</strong> — the escrowed funds are released to the seller and no refund is issued.</li>
          <li>Refunds are processed within <strong>5 to 10 business days</strong> depending on the buyer's bank or card provider.</li>
        </ul>
      </div>

      {/* 7. Swap Transaction Refunds */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faExchangeAlt} /> 7. Swap Transaction Refunds</h2>
        <p>For swap transactions:</p>
        <ul>
          <li><strong>If the dispute is ruled in the swap initiator's favour</strong> — only the swap top-up fee (the agreed payment amount) is refunded. The device offered by the initiator will be returned through the logistics process.</li>
          <li><strong>If the dispute is ruled in the seller's favour</strong> — the escrowed swap top-up funds are released to the seller.</li>
          <li>Refunds for swap transactions are processed within <strong>5 to 10 business days</strong>.</li>
        </ul>
        <div className="legal-warning">
          Note: Logistics fees are non-refundable once the delivery process has been initiated, regardless of the dispute outcome.
        </div>
      </div>

      {/* 8. Dispute Investigation Process */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faSearch} /> 8. Dispute Investigation Process</h2>
        <p>AutoboyExpress admin will investigate all disputes fairly and impartially. The investigation process includes:</p>
        <ul>
          <li>Review of all evidence submitted by the buyer and seller.</li>
          <li>Review of product listing photographs, description, and condition as posted by the seller.</li>
          <li>Review of warehouse inspection records from the AutoboyExpress inspection centre.</li>
          <li>Review of delivery confirmation and logistics records from FabsTech Express.</li>
          <li>Communication with both parties if additional information is required.</li>
        </ul>
        <p>AutoboyExpress reserves the right to make a final binding decision on all disputes. The investigation period may take <strong>up to 7 business days</strong> from the date the dispute is submitted.</p>
      </div>

      {/* 9. Fraudulent Disputes */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faExclamationTriangle} /> 9. Fraudulent Disputes</h2>
        <p>AutoboyExpress takes fraudulent dispute claims seriously. Users found to be submitting false or misleading dispute claims will face the following consequences:</p>
        <ul>
          <li>Immediate suspension of their account pending investigation.</li>
          <li>Permanent ban from the platform if fraud is confirmed.</li>
          <li>Reporting to relevant Nigerian law enforcement authorities where applicable.</li>
        </ul>
      </div>

      {/* Contact */}
      <div className="legal-contact">
        <h2><FontAwesomeIcon icon={faEnvelope} /> 10. Contact Us</h2>
        <div className="legal-contact-grid">
          <div className="legal-contact-item">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="contact-icon" />
            <span>3/9 Olukolesho Street, Tayeolu Tower, Lagos, Nigeria</span>
          </div>
          <div className="legal-contact-item">
            <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
            <a href="mailto:support@autoboyexpress.com">support@autoboyexpress.com</a>
          </div>
          <div className="legal-contact-item">
            <FontAwesomeIcon icon={faPhone} className="contact-icon" />
            <a href="tel:09136941527">09136941527</a> / <a href="tel:08110749313">08110749313</a>
          </div>
        </div>
      </div>

      <div className="legal-back-top">
        <button onClick={scrollToTop}>
          <FontAwesomeIcon icon={faArrowUp} /> Back to Top
        </button>
      </div>
    </div>
  );
};

export default RefundPolicy;
